import { useState, useEffect } from "react";
import {
  buildTimeSlotRanges,
  getSessionSlotIncrementMinutes,
} from "../utils/programTimeSlotUtils";

interface Session {
  id?: string;
  sessionId?: string;
  startDate: string;
  endDate: string;
  holidays?: string[];
  extraDates?: Array<{ date: string }>;
  dayOfWeekDateRanges?: Record<string, { startDate: string; endDate: string }>;
  dayOfWeekTimes?: Record<string, { startTime: string; endTime: string }>;
  sessionSlotType?: string;
}

export const useProgramSessionDates = (session: Session | null, selectedDate?: string) => {
  const [dateOptions, setDateOptions] = useState<string[]>([]);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);

  const pad = (n: number) => String(n).padStart(2, "0");
  const dateOnly = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const weekdayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;
  const addDays = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);

  // Build available dates when session changes
  useEffect(() => {
    if (!session) {
      setDateOptions([]);
      setTimeSlots([]);
      return;
    }

    const holidays: Set<string> = new Set(Array.isArray(session.holidays) ? session.holidays : []);
    const dates: Set<string> = new Set();

    const includeDateIfValid = (dateStr: string) => {
      if (!holidays.has(dateStr)) dates.add(dateStr);
    };

    const startWindow = new Date(session.startDate);
    const endWindow = new Date(session.endDate);

    if (session.dayOfWeekDateRanges) {
      // Only use the START dates from each day of the week
      Object.keys(session.dayOfWeekDateRanges).forEach((day) => {
        const rng = session.dayOfWeekDateRanges![day];
        if (rng?.startDate) {
          includeDateIfValid(rng.startDate);
        }
      });
    } else {
      for (let d = new Date(startWindow); d <= endWindow; d = addDays(d, 1)) {
        includeDateIfValid(dateOnly(d));
      }
    }

    if (Array.isArray(session.extraDates)) {
      session.extraDates.forEach((ed: any) => {
        if (ed?.date) dates.add(ed.date);
      });
    }

    if (dates.size === 0) {
      dates.add(dateOnly(startWindow));
    }
    const sorted = Array.from(dates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
    setDateOptions(sorted);
    setTimeSlots([]);
  }, [session]);

  // Build time slots when date is selected
  useEffect(() => {
    if (!selectedDate || !session) {
      setTimeSlots([]);
      return;
    }

    const dateStr = selectedDate;
    
    // Get times for the selected day
    const timesForDay = () => {
      const dayIdx = new Date(`${dateStr}T00:00:00`).getDay();
      const key = weekdayNames[dayIdx];
      return session.dayOfWeekTimes?.[key] || null;
    };
    
    const t = timesForDay();
    let startTime = t?.startTime;
    let endTime = t?.endTime;
    
    if (!startTime || !endTime) {
      // fallback to global times from session start/end
      const s = new Date(session.startDate);
      const e = new Date(session.endDate);
      startTime = `${pad(s.getHours())}:${pad(s.getMinutes())}`;
      endTime = `${pad(e.getHours())}:${pad(e.getMinutes())}`;
    }

    const incrementMinutes = getSessionSlotIncrementMinutes(session.sessionSlotType);
    const slots =
      incrementMinutes !== null
        ? buildTimeSlotRanges(startTime, endTime, incrementMinutes)
        : [`${startTime}-${endTime}`];
    setTimeSlots(slots);
  }, [selectedDate, session]);

  return { dateOptions, timeSlots };
};
